import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import KpiGrid from '../../components/CallcenterV2/KpiGrid';
import RealtimeBadge from '../../components/CallcenterV2/RealtimeBadge';
import { fetchKpis } from '../../services/callcenterV2/metricsService';
import { fetchPauseReasons } from '../../services/callcenterV2/pauseReasonsService';
import { pauseAgent, unpauseAgent } from '../../services/callcenterV2/agentsService';
import { ccv2Fetch, getCcv2CurrentUser } from '../../services/callcenterV2';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const formatDuration = (s) => {
  if (!s || s <= 0) return '0s';
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

function DashboardAgent() {
  const user = useCurrentUser();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({});
  const [pauseReasons, setPauseReasons] = useState([]);
  const [pauseHistory, setPauseHistory] = useState([]);
  const [agentData, setAgentData] = useState(null);
  const [selectedPauseReason, setSelectedPauseReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [error, setError] = useState(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [creatingReason, setCreatingReason] = useState(false);

  // Determine agentKey: from URL param or own JWT agentKey
  const agentKey = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('agentId') || user?.agentKey || null;
  }, [user]);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const [kpisRes, reasonsRes] = await Promise.allSettled([
        fetchKpis(agentKey),
        fetchPauseReasons(false),
      ]);
      if (kpisRes.status === 'fulfilled') setKpis(kpisRes.value || {});
      if (reasonsRes.status === 'fulfilled') setPauseReasons(Array.isArray(reasonsRes.value) ? reasonsRes.value : []);

      // Fetch agent detail if agentKey available
      if (agentKey) {
        const agentRes = await ccv2Fetch(`/v2/callcenter/agents/${encodeURIComponent(agentKey)}`).catch(() => null);
        if (agentRes) setAgentData(agentRes);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user, agentKey]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePause = useCallback(async () => {
    if (!agentKey || !selectedPauseReason) return;
    setActionLoading(true);
    try {
      await pauseAgent(agentKey, { pauseReasonId: parseInt(selectedPauseReason, 10) });
      setActionMsg('Pausa iniciada com sucesso.');
      setShowPauseModal(false);
      setSelectedPauseReason('');
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e) {
      setActionMsg(`Erro ao pausar: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  }, [agentKey, selectedPauseReason]);

  const handleUnpause = useCallback(async () => {
    if (!agentKey) return;
    setActionLoading(true);
    try {
      await unpauseAgent(agentKey);
      setActionMsg('Retorno da pausa registrado.');
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e) {
      setActionMsg(`Erro ao retornar: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  }, [agentKey]);

  const handleCreatePauseReason = useCallback(async () => {
    if (!newReasonName.trim()) return;
    setCreatingReason(true);
    try {
      await ccv2Fetch('/v2/callcenter/pause-reasons', {
        method: 'POST',
        body: JSON.stringify({ name: newReasonName.trim(), isGlobal: false }),
      });
      setNewReasonName('');
      const updated = await fetchPauseReasons(false);
      setPauseReasons(Array.isArray(updated) ? updated : []);
      setActionMsg('Motivo de pausa criado com sucesso.');
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e) {
      setActionMsg(`Erro: ${e.message}`);
    } finally {
      setCreatingReason(false);
    }
  }, [newReasonName]);

  const kpiMetrics = useMemo(() => [
    { metricKey: 'calls_answered', label_pt: 'Chamadas Hoje', value: kpis.serviced_today ?? kpis.servicedToday ?? null, unit: 'count' },
    { metricKey: 'aht', label_pt: 'Meu TMA', value: kpis.aht ?? null, unit: 'seconds' },
    { metricKey: 'asa', label_pt: 'TME Médio', value: kpis.asa ?? null, unit: 'seconds', higherIsBetter: false },
    { metricKey: 'sla', label_pt: 'SLA', value: kpis.service_level ?? kpis.sla ?? null, unit: 'percent', target: 80, warningValue: 70, criticalValue: 60, higherIsBetter: true },
  ], [kpis]);

  return (
    <div className="ccv2-page-container ccv2-p5-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Meu Dashboard</h1>
          <p>{user?.username || 'Agente'} · {agentKey ? `Chave: ${agentKey}` : 'Sem agentKey configurado'}</p>
        </div>
        <div className="ccv2-page-actions">
          <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={loadData} disabled={loading}>
            🔄 Atualizar
          </button>
        </div>
      </div>

      {actionMsg && (
        <div className={`ccv2-p5-${actionMsg.startsWith('Erro') ? 'error' : 'success'}`}>
          {actionMsg}
          <button onClick={() => setActionMsg(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {error && <div className="ccv2-p5-error">⚠️ {error}</div>}

      {loading ? (
        <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
      ) : (
        <>
          {/* ── KPIs pessoais ── */}
          <div className="ccv2-p5-section">
            <div className="ccv2-p5-section-title">📈 Meus KPIs</div>
            <KpiGrid metrics={kpiMetrics} />
          </div>

          {/* ── Ações de pausa ── */}
          {agentKey && (
            <div className="ccv2-p5-section">
              <div className="ccv2-p5-section-title">⏸ Pausar / Retornar</div>
              <div className="ccv2-p5-row">
                <button className="ccv2-p5-btn ccv2-p5-btn-warning" disabled={actionLoading} onClick={() => setShowPauseModal(true)}>
                  ⏸ Iniciar Pausa
                </button>
                <button className="ccv2-p5-btn ccv2-p5-btn-success" disabled={actionLoading} onClick={handleUnpause}>
                  ▶ Retornar da Pausa
                </button>
              </div>

              {/* Quick pause reason select */}
              {showPauseModal && (
                <div className="ccv2-p5-modal-overlay" onClick={() => setShowPauseModal(false)}>
                  <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ccv2-p5-modal-header">
                      <h3 className="ccv2-p5-modal-title">Selecione o Motivo da Pausa</h3>
                      <button className="ccv2-p5-modal-close" onClick={() => setShowPauseModal(false)}>×</button>
                    </div>
                    <div className="ccv2-p5-modal-body">
                      <div className="ccv2-p5-field">
                        <label className="ccv2-p5-label">Motivo *</label>
                        <select className="ccv2-p5-select" value={selectedPauseReason} onChange={(e) => setSelectedPauseReason(e.target.value)} style={{ width: '100%' }}>
                          <option value="">Selecione...</option>
                          {pauseReasons.filter((r) => r.is_active !== false).map((r) => (
                            <option key={r.id_pause_reason} value={r.id_pause_reason}>
                              {r.name} {r.is_global ? '(Global)' : '(Personal)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="ccv2-p5-modal-footer">
                      <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={() => setShowPauseModal(false)}>Cancelar</button>
                      <button className="ccv2-p5-btn ccv2-p5-btn-warning" disabled={!selectedPauseReason || actionLoading} onClick={handlePause}>
                        {actionLoading ? '⌛' : '⏸'} Pausar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Meus motivos de pausa (personal CRUD) ── */}
          <div className="ccv2-p5-section">
            <div className="ccv2-p5-section-title">📋 Meus Motivos de Pausa (personal)</div>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <input
                className="ccv2-p5-input"
                placeholder="Nome do novo motivo..."
                value={newReasonName}
                onChange={(e) => setNewReasonName(e.target.value)}
                style={{ maxWidth: 260 }}
              />
              <button className="ccv2-p5-btn ccv2-p5-btn-primary" onClick={handleCreatePauseReason} disabled={!newReasonName.trim() || creatingReason}>
                {creatingReason ? '⌛' : '+'} Criar
              </button>
            </div>

            <div className="ccv2-config-card">
              <div className="ccv2-config-table-wrapper">
                <table className="ccv2-config-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pauseReasons.length === 0 ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#94a3b8', padding: '1.5rem' }}>Nenhum motivo de pausa configurado.</td></tr>
                    ) : pauseReasons.filter((r) => !r.is_global).map((r) => (
                      <tr key={r.id_pause_reason}>
                        <td className="ccv2-cell-name">{r.name}</td>
                        <td><span className="ccv2-p5-badge ccv2-p5-badge-personal">Personal</span></td>
                        <td><span className={`ccv2-p5-badge ${r.is_active !== false ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>{r.is_active !== false ? 'Ativo' : 'Inativo'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardAgent;
